import { useMemo } from 'react'

import type { ToolbarActivityListItem } from '#/shared/api/generated/shipment/models'
import { useListItemsApiV1ToolbarTypeGet } from '#/shared/api/generated/shipment/theCargoShipment'
import { formatHistoryTimestamp } from '#/shared/lib/format'

const DEFAULT_ACTIVITY_FEED_SIZE = 100

export interface ShipmentActivityFeedItem {
  id: string
  eventType: string
  text: string
  timestamp: string
  occurredAt: string
  actorName: string
  isSystemActor: boolean
}

interface UseShipmentActivityFeedOptions {
  enabled?: boolean
  size?: number
}

export interface UseShipmentActivityFeedResult {
  activities: ShipmentActivityFeedItem[]
  isLoading: boolean
}

export function toShipmentActivityFeedItem(
  item: ToolbarActivityListItem,
): ShipmentActivityFeedItem {
  return {
    id: item.id,
    eventType: item.event_type,
    text: item.text,
    timestamp: item.created_at ? formatHistoryTimestamp(item.created_at) : '',
    occurredAt: item.created_at ?? '',
    actorName: item.actor.name,
    isSystemActor: item.actor.kind === 'system',
  }
}

export function useShipmentActivityFeed(
  shipmentId: string,
  {
    enabled = true,
    size = DEFAULT_ACTIVITY_FEED_SIZE,
  }: UseShipmentActivityFeedOptions = {},
): UseShipmentActivityFeedResult {
  const query = useListItemsApiV1ToolbarTypeGet(
    'activity',
    { shipment_id: shipmentId, size },
    { query: { enabled: enabled && Boolean(shipmentId) } },
  )

  const activities = useMemo<ShipmentActivityFeedItem[]>(() => {
    const items = (query.data?.data.items ??
      []) as unknown as ToolbarActivityListItem[]
    return items.map(toShipmentActivityFeedItem)
  }, [query.data])

  return {
    activities,
    isLoading: query.isLoading && enabled && Boolean(shipmentId),
  }
}
